import { lazy } from 'react'

import type { ComponentType, LazyExoticComponent } from 'react'

/**
 * `React.lazy` for a module that exports its component by **name** — the only
 * lazy idiom in `src/` (P-23 / ADR 0023).
 *
 * `export default` is banned, so a module never changes shape to suit how a
 * consumer chooses to load it; the adaptation happens here instead, at the call
 * site:
 *
 * ```ts
 * const CollectorsList = lazyNamed(
 *   () => import('@/features/integrations/collectors/components/list'),
 *   'CollectorsList',
 * )
 * ```
 *
 * **The `loader` parameter is a thunk, and must stay one.** Rolldown splits a
 * chunk on the literal `import('…')` in the *call site's* source. "Simplifying"
 * this to `lazyNamed('./x', 'X')` would make the specifier statically
 * unanalysable and silently fold the chunk back into its parent — no error, no
 * warning, just a heavier eager bundle (ADR 0023, "Why the helper takes a
 * thunk").
 *
 * A missed conversion is a `tsc` failure, never an `undefined` component in
 * front of a user: `name` is keyed to the module's exports.
 */
export function lazyNamed<
	TName extends string,
	// `ComponentType<any>` is what `React.lazy` itself is typed against; a
	// narrower bound here would reject perfectly valid prop types by
	// contravariance.
	TComponent extends ComponentType<any>,
>(
	loader: () => Promise<Record<TName, TComponent>>,
	name: TName,
): LazyExoticComponent<TComponent> {
	return lazy(() => loader().then((module) => ({ default: module[name] })))
}
